import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, ArrowRight, Save, Upload, User, FileText, CreditCard, Smartphone, MapPin, Loader2, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useRider, useUpdateRider, useUploadRiderProfilePhoto, useUploadRiderKycDocuments } from '@/api-services/hooks/useRider';
import { RiderDTO } from '@/api-services/hooks/adminTypes';
import { DatePickerController } from '@/components/ui/date-picker';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { getPhoneValidationMessage } from '@/lib/phoneValidation';
import { getDocumentViewUrl } from '@/api-services/config';
import { pricingApiClient } from '@/api-services/hooks/pricingApiClient';
import { CityMaster, StateMaster, CountryMaster } from '@/api-services/hooks/pricingTypes';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useDuplicateCheck } from '@/hooks/useDuplicateCheck';

const riderFormSchema = z.object({
  // Personal Information
  phoneNumber: z.string()
    .min(1, 'Phone number is required')
    .superRefine((val, ctx) => {
      const msg = getPhoneValidationMessage(val);
      if (msg) ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg });
    }),
  emailId: z.string().email('Invalid email address').optional().or(z.literal('')),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  userName: z.string().optional(),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional(),
  homeAddress: z.string().optional(),
  workAddress: z.string().optional(),
  countryId: z.string().optional().or(z.literal('')),
  stateId: z.string().optional().or(z.literal('')),
  cityId: z.string().optional().or(z.literal('')),
  preferredLanguage: z.string().optional(),
  profilePhotoUrl: z.string().optional().or(z.literal('')),
  phoneCountryCode: z.string().optional(),
  emergencyContactNumber: z.string().optional(),
  emergencyPhoneCountryCode: z.string().optional(),
  // KYC Information
  kycIdType: z.string().optional(),
  kycIdNumber: z.string().optional(),
  kycDocumentPhotoUrl: z.string().optional().or(z.literal('')),

  // Payment Information
  preferredPaymentMethod: z.string().min(1, 'Payment method is required'),
  walletBalance: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const n = Number(val);
      return Number.isNaN(n) ? undefined : n;
    },
    z.number().optional()
  ),
  savedPaymentMethods: z.array(z.string()).optional(),

  // Device & App Information
  deviceToken: z.string().optional(),
  locationAccessPermission: z.boolean().optional(),

  // Additional Fields
  signupMethod: z.string().optional(),
  otpVerified: z.boolean().optional(),
  promotionalConsent: z.boolean().optional(),
  notificationPreferences: z.array(z.string()).optional(),
});

type RiderFormData = z.infer<typeof riderFormSchema>;

const EditRiderPage: React.FC = () => {
  const navigate = useNavigate();
  const { riderId } = useParams<{ riderId: string }>();
  const riderIdNum = riderId ? parseInt(riderId, 10) : 0;
  const [activeTab, setActiveTab] = useState('personal');
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});

  // Location data
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [addWalletAmount, setAddWalletAmount] = useState('');
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [allStates, setAllStates] = useState<StateMaster[]>([]);
  const [allCities, setAllCities] = useState<CityMaster[]>([]);

  useEffect(() => {
    pricingApiClient.get<ApiResponse<PagedData<CountryMaster>>>('/country/list', { page: 0, size: 500 })
      .then(res => setCountries(res.data?.content || [])).catch(() => {});
    pricingApiClient.get<ApiResponse<PagedData<StateMaster>>>('/state/list', { page: 0, size: 500 })
      .then(res => setAllStates(res.data?.content || [])).catch(() => {});
    pricingApiClient.get<ApiResponse<CityMaster[]>>('/city/list-only')
      .then(res => setAllCities(res.data || [])).catch(() => {});
  }, []);

  // Fetch rider data
  const { data: riderData, loading: loadingRider, error: riderError } = useRider(riderIdNum, {
    onError: (error) => {
      toast.error(`Failed to load rider: ${error.message}`);
    },
  });

  const uploadPhotoMutation = useUploadRiderProfilePhoto();
  const uploadKycMutation = useUploadRiderKycDocuments();

  // Helper to handle file uploads after rider update
  const handlePostUpdateUploads = (riderId: number) => {
    const profilePhotoFile = uploadedFiles['profilePhotoUrl'];
    const kycDocFile = uploadedFiles['kycDocumentPhotoUrl'];
    const pendingUploads: Promise<void>[] = [];

    if (profilePhotoFile) {
      pendingUploads.push(
        new Promise((resolve, reject) => {
          uploadPhotoMutation.mutate({ riderId, file: profilePhotoFile }, {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          });
        })
      );
    }

    if (kycDocFile) {
      // Get current form values for KYC type and number
      const kycIdType = watch('kycIdType');
      const kycIdNumber = watch('kycIdNumber');
      if (kycIdType && kycIdNumber) {
        pendingUploads.push(
          new Promise((resolve, reject) => {
            uploadKycMutation.mutate({
              riderId,
              govtIdType: kycIdType,
              govtIdNumber: kycIdNumber,
              file: kycDocFile,
            }, {
              onSuccess: () => resolve(),
              onError: (err) => reject(err),
            });
          })
        );
      }
    }

    if (pendingUploads.length === 0) {
      toast.success('Rider updated successfully!');
      navigate('/admin/riders');
      return;
    }

    Promise.allSettled(pendingUploads).then((results) => {
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        toast.warning(`Rider updated but ${failures.length} file upload(s) failed`);
      } else {
        toast.success('Rider updated successfully!');
      }
      navigate('/admin/riders');
    });
  };

  const updateRiderMutation = useUpdateRider({
    onSuccess: (data, variables) => {
      handlePostUpdateUploads(riderIdNum);
    },
    onError: (error, variables) => {
      toast.error(`Failed to update rider: ${error.message}`);
    },
  });

  const updateRider = updateRiderMutation.mutate;
  const isPending = updateRiderMutation.status === 'pending' || uploadPhotoMutation.status === 'pending' || uploadKycMutation.status === 'pending';

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
    setFocus,
    reset,
    trigger,
  } = useForm<RiderFormData>({
    resolver: zodResolver(riderFormSchema),
    defaultValues: {
      phoneNumber: '',
      userName: '',
      emailId: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      homeAddress: '',
      workAddress: '',
      countryId: '',
      stateId: '',
      cityId: '',
      preferredLanguage: '',
      profilePhotoUrl: '',
      kycIdType: '',
      kycIdNumber: '',
      kycDocumentPhotoUrl: '',
      preferredPaymentMethod: '',
      walletBalance: undefined,
      savedPaymentMethods: [],
      deviceToken: '',
      locationAccessPermission: false,
      signupMethod: '',
      otpVerified: false,
      promotionalConsent: false,
      notificationPreferences: [],
      emergencyContactNumber: '',
      emergencyPhoneCountryCode: ''
    },
  });

  // Duplicate email/phone check
  const { duplicateErrors, checkEmail, checkPhone } = useDuplicateCheck('rider', riderIdNum);
  const phoneRef = React.useRef<HTMLDivElement>(null);

  // Tab field groups for per-tab validation
  const personalFields: (keyof RiderFormData)[] = [
    'phoneNumber', 'emailId', 'firstName', 'lastName',
  ];
  const kycFields: (keyof RiderFormData)[] = [
    'kycIdType', 'kycIdNumber',
  ];

  const handleSaveTab = async (fields: (keyof RiderFormData)[]) => {
    const valid = await trigger(fields);
    const hasDuplicate =
    duplicateErrors.email ||
    duplicateErrors.phone;

  if (!valid || hasDuplicate) {
    toast.error('Please fix duplicate email/phone before saving');
    return false;
  }

  toast.success('Saved successfully');
  return true;
  };

  const handleNextTab = async (fields: (keyof RiderFormData)[], nextTab: string) => {
    const valid = await trigger(fields);
  const hasDuplicate =
    duplicateErrors.email ||
    duplicateErrors.phone;

  if (!valid || hasDuplicate) {
    if (duplicateErrors.phone) {
      toast.error(duplicateErrors.phone);
      phoneRef.current?.querySelector('input')?.focus();
    } else if (duplicateErrors.email) {
      toast.error(duplicateErrors.email);
      setFocus('emailId');
    }
    return;
  }

  setActiveTab(nextTab);
  };

  // Cascading location filters
  const selectedCountryId = watch('countryId');
  const selectedStateId = watch('stateId');
  const filteredStates = selectedCountryId
    ? allStates.filter(s => s.country?.countryId?.toString() === selectedCountryId) : [];
  const filteredCities = selectedStateId
    ? allCities.filter(c => c.state?.stateId?.toString() === selectedStateId) : [];

  // Populate form when rider data is loaded
  // Wait for both rider data AND location lists before populating form
const locationDataLoaded =
  countries.length > 0 &&
  allStates.length > 0 &&
  allCities.length > 0;

  useEffect(() => {
    if (riderData && locationDataLoaded) {

      const fullNumber = riderData.phoneCountryCode && riderData.phoneNumber
  ? `${riderData.phoneCountryCode}${riderData.phoneNumber}`
  : riderData.phoneNumber || '';

  const fullEmergencyNumber = riderData.emergencyPhoneCountryCode && riderData.emergencyContactNumber
  ? `${riderData.emergencyPhoneCountryCode}${riderData.emergencyContactNumber}`
  : riderData.emergencyContactNumber || '';

      reset({
        phoneNumber: fullNumber, // ✅ E.164 format
        phoneCountryCode: riderData.phoneCountryCode || '',
        emergencyContactNumber: fullEmergencyNumber, // ✅ E.164 format
        emergencyPhoneCountryCode: riderData.emergencyPhoneCountryCode || '',
        emailId: riderData.emailId || '',
        userName: riderData.userName || '',
        firstName: riderData.firstName || riderData.fullName?.split(' ')[0] || '',
        lastName: riderData.lastName || riderData.fullName?.split(' ').slice(1).join(' ') || '',
        dateOfBirth: riderData.dateOfBirth || '',
        gender: riderData.gender?.toUpperCase() || '',
        homeAddress: riderData.homeAddress || '',
        workAddress: riderData.workAddress || '',
        countryId: riderData.countryId?.toString() || '',
        stateId: riderData.stateId?.toString() || '',
        cityId: riderData.cityId?.toString() || '',
        preferredLanguage: riderData.preferredLanguage || '',
        profilePhotoUrl: riderData.profilePhotoUrl || '',
        kycIdType: riderData.kycIdType?.toUpperCase() || '',
        kycIdNumber: riderData.kycIdNumber ?? '',
        kycDocumentPhotoUrl: riderData.kycDocumentPhotoUrl ?? '',
        preferredPaymentMethod: riderData.preferredPaymentMethod?.toUpperCase() || '',
        walletBalance: riderData.walletBalance != null ? Number(riderData.walletBalance) : undefined,
        savedPaymentMethods: riderData.savedPaymentMethods ?? [],
        deviceToken: riderData.deviceToken ?? '',
        locationAccessPermission: riderData.locationAccessPermission ?? false,
        signupMethod: riderData.signupMethod?.toUpperCase() || '',
        otpVerified: riderData.otpVerified ?? false,
        promotionalConsent: riderData.promotionalConsent ?? false,
        notificationPreferences: riderData.notificationPreferences ?? [],
      });
      // Trigger validation after resetting form with data
      trigger();
    }
  }, [riderData, locationDataLoaded, reset, trigger]);

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof RiderFormData
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setValue(fieldName as any, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper function to render file upload field with URL option
  const renderDocumentField = (
    fieldName: keyof RiderFormData,
    label: string,
    placeholder: string
  ) => {
    const hasFile = uploadedFiles[fieldName as string];
    const currentValue = watch(fieldName);

    // For profile photo and KYC document, show image preview
    const isImageField = fieldName === 'profilePhotoUrl' || fieldName === 'kycDocumentPhotoUrl';
    const showImagePreview = isImageField && currentValue &&
      (currentValue.startsWith('data:') || currentValue.startsWith('http') || currentValue.startsWith('/'));

    return (
      <div className="space-y-2 md:col-span-2">
        <Label>{label}</Label>
        <div className="flex items-center gap-4">
          {/* Image preview */}
          {showImagePreview && (
            <img
              src={currentValue.startsWith('http') || currentValue.startsWith('data:') ? currentValue : getDocumentViewUrl(currentValue)}
              alt={label}
              className="w-16 h-16 object-cover rounded-md border"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          )}
          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload(e, fieldName)}
              className="w-auto text-sm h-8"
            />
            {hasFile && (
              <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                {hasFile.name}
              </span>
            )}
          </div>
        </div>
        {errors[fieldName] && (
          <p className="text-sm text-destructive">{errors[fieldName]?.message}</p>
        )}
      </div>
    );
  };

  const onSubmit = (data: RiderFormData) => {
    if (!riderIdNum) {
      toast.error('Rider ID is missing');
      return;
    }

    // Don't send base64 photos in JSON - use multipart upload instead
    // to avoid 413 Payload Too Large error
    const hasProfilePhotoFile = !!uploadedFiles['profilePhotoUrl'];
    const hasKycDocFile = !!uploadedFiles['kycDocumentPhotoUrl'];

      let phoneCountryCode = '';
      let mobileNumber = '';
    
      if (data.phoneNumber) {
        const phone = parsePhoneNumberFromString(data.phoneNumber);
        
        if (phone) {
          phoneCountryCode = `+${phone.countryCallingCode}`; // +91
          mobileNumber = phone.nationalNumber;              // 9876543210
        }
      }

      let emergencyContactNumber = '';
      let emergencyPhoneCountryCode = '';
        
      if (data.emergencyContactNumber) {
        const phone = parsePhoneNumberFromString(data.emergencyContactNumber);
        if (phone) {
          emergencyPhoneCountryCode = `+${phone.countryCallingCode}`; // +91
          emergencyContactNumber = phone.nationalNumber;              // 9876543210
        }
      }

    // Build update payload — preserve all fields so the API doesn't clear them
    const riderUpdateData: RiderDTO = {
      riderId: riderIdNum,
      phoneNumber: mobileNumber,
      phoneCountryCode: phoneCountryCode,
      emergencyContactNumber: emergencyContactNumber,
      emergencyPhoneCountryCode: emergencyPhoneCountryCode,
      emailId: data.emailId?.trim() || riderData?.emailId || '',
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      fullName: `${data.firstName.trim()} ${data.lastName.trim()}`,
      dateOfBirth: data.dateOfBirth || riderData?.dateOfBirth || '',
      gender: data.gender || riderData?.gender || '',
      homeAddress: data.homeAddress?.trim() || riderData?.homeAddress || '',
      workAddress: data.workAddress?.trim() || riderData?.workAddress || '',
      countryId: data.countryId ? parseInt(data.countryId) : (riderData?.countryId || undefined),
      stateId: data.stateId ? parseInt(data.stateId) : (riderData?.stateId || undefined),
      cityId: data.cityId ? parseInt(data.cityId) : (riderData?.cityId || undefined),
      preferredLanguage: data.preferredLanguage || riderData?.preferredLanguage || '',
      // Only send profile photo URL if it's an external URL or server path, not base64
      profilePhotoUrl: hasProfilePhotoFile ? (riderData?.profilePhotoUrl || '') : (data.profilePhotoUrl?.trim() || riderData?.profilePhotoUrl || ''),
      kycIdType: data.kycIdType?.trim() || riderData?.kycIdType || '',
      kycIdNumber: data.kycIdNumber?.trim() || riderData?.kycIdNumber || '',
      kycDocumentPhotoUrl: hasKycDocFile ? (riderData?.kycDocumentPhotoUrl || '') : (data.kycDocumentPhotoUrl?.trim() || riderData?.kycDocumentPhotoUrl || ''),
      preferredPaymentMethod: data.preferredPaymentMethod || riderData?.preferredPaymentMethod || '',
      walletBalance: data.walletBalance != null ? Number(data.walletBalance) : (riderData?.walletBalance ?? 0),
      savedPaymentMethods: data.savedPaymentMethods || riderData?.savedPaymentMethods || [],
      deviceToken: data.deviceToken?.trim() || riderData?.deviceToken || '',
      locationAccessPermission: data.locationAccessPermission ?? riderData?.locationAccessPermission ?? false,
      signupMethod: data.signupMethod || riderData?.signupMethod || '',
      otpVerified: data.otpVerified ?? riderData?.otpVerified ?? false,
      promotionalConsent: data.promotionalConsent ?? riderData?.promotionalConsent ?? false,
      notificationPreferences: data.notificationPreferences || riderData?.notificationPreferences || [],
      isDeletedValue: riderData?.isDeletedValue ?? false,
      status: riderData?.status || 'ACTIVE',
    };

    updateRider({ id: riderIdNum, data: riderUpdateData });
  };

  if (loadingRider) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading rider details...</p>
        </div>
      </div>
    );
  }

  if (riderError || !riderData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-destructive mb-4">
              {riderError?.message || 'Rider not found'}
            </p>
            <Button onClick={() => navigate('/admin/riders')}>Back to Riders</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/riders')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Edit Rider</h1>
            <p className="text-muted-foreground mt-1">Update rider information</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, (formErrors) => {
        const errorFields = Object.keys(formErrors);
        if (errorFields.length > 0) {
          toast.error(`Please fix validation errors: ${errorFields.join(', ')}`);
        }
      })}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="personal">
              <User className="w-4 h-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger value="kyc">
              <FileText className="w-4 h-4 mr-2" />
              KYC
            </TabsTrigger>
            <TabsTrigger value="payment">
              <CreditCard className="w-4 h-4 mr-2" />
              Payment
            </TabsTrigger>
          </TabsList>

          {/* Personal Information Tab */}
          <TabsContent value="personal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update the rider's personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone Number *</Label>
                    <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <PhoneInput
                          ref={phoneRef}
                          id="phoneNumber"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={async () => {                           
                              const rawInput = field.value;                            
                              const parsedInput = parsePhoneNumberFromString(rawInput);
                              const parsedExisting = parsePhoneNumberFromString(riderData?.phoneNumber || '');
                              const inputNational = parsedInput?.nationalNumber || '';
                              const existingNational = parsedExisting?.nationalNumber || riderData?.phoneNumber || '';
                              if (inputNational === existingNational) return;                            
                              const dup = await checkPhone(inputNational);
                            if (dup) phoneRef.current?.querySelector('input')?.focus();
                          }}
                          hasError={!!(errors.phoneNumber || duplicateErrors.phone)}
                        />
                      )}
                    />
                    {(errors.phoneNumber || duplicateErrors.phone) && (
                      <p className="text-sm text-destructive">{errors.phoneNumber?.message || duplicateErrors.phone}</p>
                    )}
                  </div>
                     <div className="space-y-2">
                    <Label htmlFor="emergencyContactNumber">Emergency Contact Number</Label>
                    <Controller
                      control={control}
                      name="emergencyContactNumber"
                      render={({ field }) => (
                        <PhoneInput
                          id="emergencyContactNumber"
                          value={field.value}
                          onChange={field.onChange}
                         
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emailId">Email</Label>
                    <Input
                      id="emailId"
                      type="email"
                      {...register('emailId', {
                        onBlur: async () => {
                          if(riderData.emailId === getValues('emailId')) return;
                          const dup = await checkEmail(getValues('emailId'));
                          if (dup) setFocus('emailId');
                        },
                      })}
                      placeholder="rider@example.com"
                    />
                    {(errors.emailId || duplicateErrors.email) && (
                      <p className="text-sm text-destructive">{errors.emailId?.message || duplicateErrors.email}</p>
                    )}
                  </div>
                   <div className="space-y-2">
                    <Label htmlFor="userName">User Name</Label>
                    <Input
                      id="userName"
                      type="text"
                      {...register('userName')}
                      readOnly
                      placeholder="rider@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      {...register('firstName')}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-sm text-destructive">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      {...register('lastName')}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive">{errors.lastName.message}</p>
                    )}
                  </div>

                  <DatePickerController
                    control={control}
                    name="dateOfBirth"
                    label="Date of Birth"
                    placeholder="Select date of birth"
                    maxDate={new Date()} // Prevent future dates
                  />

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Controller
                      control={control}
                      name="gender"
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MALE">Male</SelectItem>
                            <SelectItem value="FEMALE">Female</SelectItem>
                            {/* <SelectItem value="OTHER">Other</SelectItem>
                            <SelectItem value="PREFER_NOT_TO_SAY">Prefer not to say</SelectItem> */}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="homeAddress">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Home Address
                    </Label>
                    <Input
                      id="homeAddress"
                      {...register('homeAddress')}
                      placeholder="123 Home St, City, State, ZIP"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="workAddress">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Work Address
                    </Label>
                    <Input
                      id="workAddress"
                      {...register('workAddress')}
                      placeholder="456 Work Ave, City, State, ZIP"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Controller control={control} name="countryId"
                      render={({ field }) => (
                        <Select 
                         key={watch('countryId')}
                        onValueChange={(val) => {
                          field.onChange(val);
                          setValue('stateId', '');
                          setValue('cityId', '');
                        }} value={field.value} disabled={countries.length === 0}>
                          <SelectTrigger><SelectValue placeholder={countries.length === 0 ? "No countries available" : "Select country"} /></SelectTrigger>
                          <SelectContent>
                            {countries.map(c => (
                              <SelectItem key={c.countryId} value={c.countryId.toString()}>
                                {c.countryName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Controller control={control} name="stateId"
                      render={({ field }) => (
                        <Select   key={watch('stateId')} onValueChange={(val) => {
                          field.onChange(val);
                          setValue('cityId', '');
                        }} value={field.value} disabled={!selectedCountryId || filteredStates.length === 0}>
                          <SelectTrigger><SelectValue placeholder={!selectedCountryId ? "Select a country first" : filteredStates.length === 0 ? "No states available" : "Select state"} /></SelectTrigger>
                          <SelectContent>
                            {filteredStates.map(s => (
                              <SelectItem key={s.stateId} value={s.stateId.toString()}>
                                {s.stateName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Controller control={control} name="cityId"
                      render={({ field }) => (
                        <Select  key={watch('cityId')} onValueChange={field.onChange} value={field.value} disabled={!selectedStateId || filteredCities.length === 0}>
                          <SelectTrigger><SelectValue placeholder={!selectedStateId ? "Select a state first" : filteredCities.length === 0 ? "No cities available" : "Select city"} /></SelectTrigger>
                          <SelectContent>
                            {filteredCities.map(c => (
                              <SelectItem key={c.cityId} value={c.cityId.toString()}>
                                {c.cityName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredLanguage">Preferred Language</Label>
                    <Controller control={control} name="preferredLanguage"
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="English">English</SelectItem>
                            <SelectItem value="Spanish">Spanish</SelectItem>
                            <SelectItem value="French">French</SelectItem>
                            {/* <SelectItem value="de">German</SelectItem> */}
                            {/* <SelectItem value="hi">Hindi</SelectItem> */}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {renderDocumentField(
                    'profilePhotoUrl',
                    'Profile Photo',
                    'https://example.com/photo.jpg'
                  )}
                </div>
              </CardContent>
            </Card>
            <div className="flex items-center justify-end space-x-4 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/riders')} disabled={isPending}>
                Cancel
              </Button>
              <Button type="button" variant="outline" onClick={() => handleSaveTab(personalFields)} disabled={isPending}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button type="button" className="bg-gradient-primary hover:opacity-90" onClick={() => handleNextTab(personalFields, 'kyc')} disabled={isPending}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>KYC Information</CardTitle>
                <CardDescription>Update Know Your Customer verification details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="kycIdType">KYC ID Type</Label>
                    <Controller
                      control={control}
                      name="kycIdType"
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ''}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select ID type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PASSPORT">Passport</SelectItem>
                            <SelectItem value="NATIONAL_ID">National ID</SelectItem>
                            <SelectItem value="DRIVING_LICENSE">Driving License</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kycIdNumber">KYC ID Number</Label>
                    <Input
                      id="kycIdNumber"
                      {...register('kycIdNumber')}
                      placeholder="ID number"
                    />
                  </div>

                  {renderDocumentField(
                    'kycDocumentPhotoUrl',
                    'KYC Document Photo',
                    'https://example.com/kyc-doc.jpg'
                  )}
                </div>
              </CardContent>
            </Card>
            <div className="flex items-center justify-end space-x-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setActiveTab('personal')} disabled={isPending}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button type="button" variant="outline" onClick={() => handleSaveTab(kycFields)} disabled={isPending}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button type="button" className="bg-gradient-primary hover:opacity-90" onClick={() => handleNextTab(kycFields, 'payment')} disabled={isPending}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Information</CardTitle>
                <CardDescription>Update payment preferences and wallet details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="preferredPaymentMethod">Preferred Payment Method <span className="text-destructive">*</span></Label>
                    <Controller
                      control={control}
                      name="preferredPaymentMethod"
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className={errors.preferredPaymentMethod ? 'border-destructive' : ''}>
                            <SelectValue placeholder="Select payment method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                            <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
                            <SelectItem value="DIGITAL_WALLET">Digital Wallet</SelectItem>
                            <SelectItem value="CASH">Cash</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.preferredPaymentMethod && (
                      <p className="text-sm text-destructive">{errors.preferredPaymentMethod.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="walletBalance">Wallet Balance</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        id="walletBalance"
                        type="number"
                        {...register('walletBalance')}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        readOnly
                        className="w-32"
                      />
                      {!showAddWallet ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddWallet(true)}
                        >
                          <Wallet className="w-4 h-4 mr-1" />
                          Add to Wallet
                        </Button>
                      ) : (
                        <>
                          <Input
                            type="number"
                            placeholder="Enter amount"
                            min="0"
                            step="0.01"
                            value={addWalletAmount}
                            onChange={(e) => setAddWalletAmount(e.target.value)}
                            className="w-32"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const amt = Number(addWalletAmount);
                              if (addWalletAmount && !isNaN(amt) && amt > 0) {
                                const current = getValues('walletBalance') || 0;
                                setValue('walletBalance', current + amt);
                                setAddWalletAmount('');
                                setShowAddWallet(false);
                              }
                            }}
                          >
                            Add
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAddWalletAmount('');
                              setShowAddWallet(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                    {errors.walletBalance && (
                      <p className="text-sm text-destructive">{errors.walletBalance.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="flex items-center justify-end space-x-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setActiveTab('kyc')} disabled={isPending}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                type="submit"
                className="bg-gradient-primary hover:opacity-90"
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Rider
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

        </Tabs>
      </form>
    </div>
  );
};

export default EditRiderPage;

